"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  ERROR_NOTICE,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type AttributeWrap,
  beautifyHtml,
  type FormatResult,
  minifyHtml,
} from "@/lib/tool-kit/developer/html/format";
import type { HtmlIssue } from "@/lib/tool-kit/developer/html/tree";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const HTML_FORMATTER_STRINGS = {
  inputLabel: "HTML",
  inputHint:
    "Formats as you type, entirely in your browser — nothing is uploaded.",
  sample:
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>Reading list</title></head><body><main><h1>Reading list</h1><p>Two <em>short</em> classics for the <a href="/seminar">seminar</a>:</p><ul><li>George Orwell, <cite>Politics and the English Language</cite><li>William Strunk Jr. &amp; E. B. White, <cite>The Elements of Style</cite></ul><pre>  spacing   kept\n    exactly</pre></main></body></html>',
  modeLabel: "Mode",
  modes: {
    beautify: "Beautify",
    minify: "Minify",
  },
  indentLabel: "Indent",
  indents: {
    two: "2 spaces",
    four: "4 spaces",
    tab: "Tab",
  },
  wrapLabel: "Attributes on block elements",
  wraps: {
    never: "Keep on the tag's line",
    auto: "Wrap tags longer than 80 characters",
    always: "One per line (2 or more)",
  },
  removeComments: "Remove comments (conditional comments are kept)",
  results: "Result",
  beautifiedLabel: "Formatted HTML",
  minifiedLabel: "Minified HTML",
  copy: "Copy HTML",
  copied: "Copied!",
  download: "Download .html",
  downloadFilename: "formatted.html",
  emptyPrompt: "Paste HTML above to format or minify it.",
  statElements: "Elements",
  statCharacters: "Characters",
  statChange: "Change in size",
  problemsHeading: "Problems found",
  noProblems: "No unclosed, mismatched or stray tags.",
  problemCount: "one: {count} problem|other: {count} problems",
  issueUnclosed: "Line {line}: <{tag}> is never closed.",
  issueStray: "Line {line}: </{tag}> has no matching start tag.",
  issueMisnested:
    "Line {line}: </{tag}> closes <{inner}> (opened on line {innerLine}) before </{inner}>.",
  issueUnterminated:
    "Line {line}: the <{tag}> tag runs to the end of the input without its closing >.",
  issueComment:
    "Line {line}: a comment opened with <!-- is never closed with -->.",
  issueSelfClosing:
    "Line {line}: <{tag}/> — browsers ignore the slash on a non-void element, so <{tag}> stays open.",
  moreProblems:
    "one: …and {count} more problem|other: …and {count} more problems",
} as const;

type Strings = typeof HTML_FORMATTER_STRINGS;
type Mode = "beautify" | "minify";
type IndentChoice = "two" | "four" | "tab";

const MODES: readonly Mode[] = ["beautify", "minify"];
const INDENTS: readonly IndentChoice[] = ["two", "four", "tab"];
const WRAPS: readonly AttributeWrap[] = ["never", "auto", "always"];
const INDENT_TEXT: Record<IndentChoice, string> = {
  two: "  ",
  four: "    ",
  tab: "\t",
};
const LINE_LENGTH = 80;
const MAX_LISTED_PROBLEMS = 50;

function issueText(issue: HtmlIssue, strings: Strings): string {
  switch (issue.kind) {
    case "unclosed":
      return t(strings.issueUnclosed, { line: issue.line, tag: issue.tag });
    case "stray":
      return t(strings.issueStray, { line: issue.line, tag: issue.tag });
    case "misnested":
      return t(strings.issueMisnested, {
        line: issue.line,
        tag: issue.tag,
        inner: issue.inner,
        innerLine: issue.innerLine,
      });
    case "selfClosing":
      return t(strings.issueSelfClosing, { line: issue.line, tag: issue.tag });
    default:
      return issue.tag === "!--"
        ? t(strings.issueComment, { line: issue.line })
        : t(strings.issueUnterminated, { line: issue.line, tag: issue.tag });
  }
}

function Problems({
  issues,
  strings,
}: {
  issues: HtmlIssue[];
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (issues.length === 0) {
    return <Notice>{strings.noProblems}</Notice>;
  }
  const hidden = issues.length - MAX_LISTED_PROBLEMS;
  return (
    <div className={`${ERROR_NOTICE} space-y-2`}>
      <p className="font-medium">
        {tPlural(strings.problemCount, locale, issues.length, {
          count: fmt(issues.length),
        }).trim()}
      </p>
      <ul className="list-disc space-y-1 ps-5">
        {issues.slice(0, MAX_LISTED_PROBLEMS).map((issue, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: problems are positional; two can share a line and a tag
          <li key={index}>{issueText(issue, strings)}</li>
        ))}
      </ul>
      {hidden > 0 ? (
        <p>
          {tPlural(strings.moreProblems, locale, hidden, {
            count: fmt(hidden),
          }).trim()}
        </p>
      ) : null}
    </div>
  );
}

export default function HtmlFormatterWidget() {
  const strings = useToolUi(HTML_FORMATTER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<string>(strings.sample);
  const [mode, setMode] = useState<Mode>("beautify");
  const [indent, setIndent] = useState<IndentChoice>("two");
  const [wrap, setWrap] = useState<AttributeWrap>("auto");
  const [removeComments, setRemoveComments] = useState(true);
  const deferred = useDeferredValue(input);

  const result: FormatResult | null = useMemo(() => {
    if (!deferred.trim()) {
      return null;
    }
    return mode === "beautify"
      ? beautifyHtml(deferred, {
          indent: INDENT_TEXT[indent],
          wrapAttributes: wrap,
          lineLength: LINE_LENGTH,
        })
      : minifyHtml(deferred, { removeComments });
  }, [deferred, mode, indent, wrap, removeComments]);

  const change =
    result && deferred.length > 0
      ? (result.output.length - deferred.length) / deferred.length
      : 0;

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="hfmt-input"
        label={strings.inputLabel}
        onChange={setInput}
        rows={12}
        value={input}
      />
      <div className={`${CARD} space-y-4`}>
        <PillGroup
          label={strings.modeLabel}
          name="hfmt-mode"
          onChange={setMode}
          options={MODES.map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        {mode === "beautify" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="hfmt-indent"
              label={strings.indentLabel}
              onChange={setIndent}
              options={INDENTS.map((value) => ({
                value,
                label: strings.indents[value],
              }))}
              value={indent}
            />
            <SelectField
              id="hfmt-wrap"
              label={strings.wrapLabel}
              onChange={setWrap}
              options={WRAPS.map((value) => ({
                value,
                label: strings.wraps[value],
              }))}
              value={wrap}
            />
          </div>
        ) : (
          <Checkbox
            checked={removeComments}
            label={strings.removeComments}
            onChange={setRemoveComments}
          />
        )}
      </div>
      <div aria-live="polite" className="space-y-4">
        {result === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                label={strings.statElements}
                value={fmt(result.elements)}
              />
              <StatCard
                emphasis
                label={strings.statCharacters}
                value={fmt(result.output.length)}
              />
              <StatCard
                label={strings.statChange}
                value={fmt(change, {
                  style: "percent",
                  maximumFractionDigits: 1,
                  signDisplay: "exceptZero",
                })}
              />
            </StatGrid>
            <div className="space-y-2">
              <label className={LABEL} htmlFor="hfmt-output">
                {mode === "beautify"
                  ? strings.beautifiedLabel
                  : strings.minifiedLabel}
              </label>
              <textarea
                className={TEXTAREA}
                id="hfmt-output"
                readOnly
                rows={14}
                spellCheck={false}
                value={result.output}
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={result.output}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    strings.downloadFilename,
                    result.output,
                    "text/html;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.download}
              </button>
            </div>
            <h3 className={WIDGET_H3}>{strings.problemsHeading}</h3>
            <Problems issues={result.issues} strings={strings} />
          </>
        )}
      </div>
    </section>
  );
}
